// Public Quiz module data hook. Fetches the three datasets from
// /api/quiz and reshapes them into the in-component shapes the existing
// Quiz screens expect (string-keyed Questions, MPs, and a per-MP
// AnswerMap).
//
// SWR with revalidateOnFocus so editor saves on /admin/quiz appear in
// the public quiz the next time the user re-focuses the tab.

import useSWR from 'swr';
import type {
  CandidateRow,
  QuizQuestionRow,
  QuizResponseRow,
} from '../db/types';

// ─── Shapes consumed by Quiz.tsx ───────────────────────────────────

export interface QuizQuestion {
  id: string; // string form of the DB id, used as map key throughout
  axis: 'GS' | 'PA';
  growth: number;
  state: number;
  text: string;
  topic: string;
}

export interface QuizMP {
  id: string;
  name: string;
  party: 'PL' | 'PN' | 'AD' | 'IND' | 'MOM' | 'AM' | 'IMP';
  role: string;
  district: number | null;
  district_2: number | null;
}

/** Per-MP answer maps: ANSWERS[mpId][questionId] = 1..5 (or undefined if
 *  the MP did not respond to that question). */
export type AnswerMap = Record<string, number>;
export type AnswersByMP = Record<string, AnswerMap>;

export interface QuizData {
  questions: QuizQuestion[];
  mps: QuizMP[];
  answers: AnswersByMP;
}

// ─── Hook ──────────────────────────────────────────────────────────

interface ApiResponse {
  questions: QuizQuestionRow[];
  candidates: CandidateRow[];
  responses: QuizResponseRow[];
  fetchedAt: string;
}

const fetcher = async (url: string): Promise<ApiResponse> => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ApiResponse;
};

export interface UseQuizResult {
  data: QuizData | null;
  isLoading: boolean;
  error: Error | null;
}

export function useQuiz(): UseQuizResult {
  const { data, error, isLoading } = useSWR<ApiResponse>('/api/quiz', fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 30_000,
    errorRetryCount: 2,
  });

  if (error) {
    return { data: null, isLoading: false, error };
  }
  if (!data) {
    return { data: null, isLoading, error: null };
  }

  const reshape: QuizData = (() => {
    // Skip questions whose axis is null — they can't contribute to the
    // compass projection. After running ⚖ Restore weights from seed in
    // /admin/quiz this should be empty, but we filter defensively in
    // case someone uploads new questions without weights.
    const questions: QuizQuestion[] = data.questions
      .filter((q) => q.axis === 'GS' || q.axis === 'PA')
      .sort((a, b) => a.question_order - b.question_order || a.id - b.id)
      .map((q) => ({
        id: String(q.id),
        axis: q.axis as 'GS' | 'PA',
        growth: q.growth_weight ?? 0,
        state: q.state_weight ?? 0,
        text: q.question_text,
        topic: q.topic ?? '',
      }));

    const mps: QuizMP[] = data.candidates.map((c) => ({
      id: String(c.id),
      name: c.full_name,
      party: normaliseParty(c.party),
      role: c.role ?? '',
      // Force district fields to JS numbers in case the DB driver hands
      // them back as strings — the downstream district filter uses
      // strict equality and would silently fail on type drift.
      district: toDistrictNumber(c.district),
      district_2: toDistrictNumber(c.district_2),
    }));

    const answers: AnswersByMP = {};
    for (const mp of mps) answers[mp.id] = {};
    for (const r of data.responses) {
      const mpKey = String(r.candidate_id);
      const qKey = String(r.question_id);
      if (!answers[mpKey]) answers[mpKey] = {};
      if (r.answer_numeric != null) answers[mpKey][qKey] = r.answer_numeric;
    }

    return { questions, mps, answers };
  })();

  return { data: reshape, isLoading: false, error: null };
}

// Maps the canonical party name stored in `candidates.party` (see
// api/_lib/parties.ts → CanonicalParty) to the short code the Quiz
// module's UI uses for compass dots, chips, and legend rows. DB-side
// rows are written as full names ('Aħwa Maltin', 'Momentum'); the
// rest of the Quiz code keys off the short codes returned here.
function normaliseParty(p: string): 'PL' | 'PN' | 'AD' | 'IND' | 'MOM' | 'AM' | 'IMP' {
  const u = (p ?? '').toUpperCase().trim();
  if (u === 'PL' || u === 'PN' || u === 'AD' || u === 'IND') return u;
  if (u === 'ADPD') return 'AD';
  if (u === 'MOM' || u === 'MOMENTUM') return 'MOM';
  if (u === 'AM' || u === 'AĦWA MALTIN' || u === 'AHWA MALTIN') return 'AM';
  if (u === 'IMP' || u.includes('IMPERIUM')) return 'IMP';
  return 'IND';
}

function toDistrictNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n >= 1 && n <= 13 ? n : null;
}
